import { setIn } from 'timm';
import { all, call } from 'typed-redux-saga';
import {
  AsyncActionState,
  asyncActionStateMatchers,
  invoke,
  SagaGenerator,
  setupSlice,
} from '@owl-frontend/redux';
import filesApiClient, {
  FILE_CONTENT_TIMEOUT_LIMIT,
} from '../../api/files/client';
import type { ClaimFile } from '../../api/files/interface';

function* deleteFiles(action: {
  payload: { dossierId: string; fileIds: string[] };
}): SagaGenerator<string[]> {
  const { fileIds } = action.payload;
  yield* all(fileIds.map((fileId) => call(filesApiClient.deleteFile, fileId)));
  return fileIds;
}

export interface FilesState {
  files: {
    [dossier_id: string]: Record<string, ClaimFile>;
  };
  filesContent: {
    [fileId: string]: {
      [pageNumber: string]: {
        url: string;
        requestedDate: number;
      };
    };
  };
  filesOCR: {
    [fileId: string]: {
      [dataVersion: string]: {
        [pageNumber: string]: string;
      };
    };
  };
  filesNotepad?: {
    [fileId: string]: {
      [dataVersion: string]: { [k: string]: string };
    };
  };
  fileSource: {
    [fileId: string]: string;
  };
  audioTranscripts: {
    [fileId: string]: string;
  };
  actions: {
    files: AsyncActionState<ClaimFile[]>;
    fetchFileContents: AsyncActionState;
    fetchFilesOCR: AsyncActionState;
    fetchFileNotepads: AsyncActionState;
    fetchFileSource: AsyncActionState<{ url: string }>;
    fetchAudioSource: AsyncActionState<{ url: string }>;
    fetchAudioTranscript: AsyncActionState;
    deleteFiles: AsyncActionState;
  };
}

const initialState: FilesState = {
  files: {},
  filesContent: {},
  filesOCR: {},
  fileSource: {},
  audioTranscripts: {},
  actions: {
    files: { status: 'uninitialized' },
    fetchFileContents: { status: 'uninitialized' },
    fetchFilesOCR: { status: 'uninitialized' },
    fetchFileNotepads: { status: 'uninitialized' },
    fetchFileSource: { status: 'uninitialized' },
    fetchAudioSource: { status: 'uninitialized' },
    fetchAudioTranscript: { status: 'uninitialized' },
    deleteFiles: { status: 'uninitialized' },
  },
};

const slice = setupSlice('files', initialState)
  .addAsyncSagas({
    files: invoke(filesApiClient.fetch),
    downloadFile: invoke(filesApiClient.downloadFile),
    deleteFiles,
    fetchFileSource: invoke(filesApiClient.fetchFileSource),
    fetchAudioSource: invoke(filesApiClient.fetchFileSource),
    fetchAudioTranscript: invoke(filesApiClient.fetchAudioTranscript),
  })
  .addAsyncSagas(
    {
      fetchFileContents: invoke(filesApiClient.fetchFilePage),
      fetchFilesOCR: invoke(filesApiClient.fetchFilesOCR),
      fetchFileNotepads: invoke(filesApiClient.fetchFileNotepads),
    },
    {
      timeoutLimit: FILE_CONTENT_TIMEOUT_LIMIT,
    }
  )
  .addReducers({
    'files/fulfilled': (state, action) => {
      const dossier_id = action.meta.arg;
      state.files[dossier_id] = action.payload.reduce((acc, file) => {
        acc[file.file_id] = file;
        return acc;
      }, {} as Record<string, ClaimFile>);
    },
    'fetchFileContents/fulfilled': (state, action) => {
      const pages = action.payload.pages;
      for (const payload of pages) {
        state.filesContent = setIn(
          state.filesContent,
          [action.meta.arg.fileId, payload.page],
          {
            url: payload.url,
            requestedDate: new Date().getTime(),
          }
        ) as typeof state.filesContent;
      }
    },
    'fetchFileSource/fulfilled': (state, action) => {
      state.filesContent = setIn(
        state.filesContent,
        [action.meta.arg.fileId, 1],
        {
          url: action.payload.url,
          requestedDate: new Date().getTime(),
        }
      ) as typeof state.filesContent;
    },
    'fetchAudioSource/fulfilled': (state, action) => {
      state.fileSource[action.meta.arg.fileId] = action.payload.url;
    },
    'fetchAudioTranscript/fulfilled': (state, action) => {
      state.audioTranscripts[action.meta.arg.fileId] = action.payload;
    },
    'fetchFilesOCR/fulfilled': (state, action) => {
      state.filesOCR = setIn(
        state.filesOCR,
        [action.meta.arg.fileId, action.meta.arg.dataVersion],
        action.payload
      ) as typeof state.filesOCR;
    },
    'fetchFileNotepads/fulfilled': (state, action) => {
      state.filesNotepad = setIn(
        state.filesNotepad,
        [action.meta.arg.fileId, action.meta.arg.dataVersion],
        action.payload
      ) as typeof state.filesNotepad;
    },
    'downloadFile/fulfilled': (state, action) => {
      window.open(action.payload.url);
    },
    'deleteFiles/fulfilled': (state, action) => {
      const { dossierId, fileIds } = action.meta.arg;
      if (state.files[dossierId]) {
        for (const fileId of fileIds) {
          delete state.files[dossierId][fileId];
        }
      }
    },
  });

export const actions = slice.actions;
export default slice.addExtra(asyncActionStateMatchers(actions).all());
